import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";

import moment from "moment";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  settingsRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    padding: theme.spacing(2),
  },
  alert: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  danger: {
    backgroundColor: "rgba(244, 67, 54, 0.12)",
  },
  info: {
    backgroundColor: "rgba(33, 150, 243, 0.10)",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
}));

const TOPUP_STATUS = {
  awaiting_payment: { labelKey: "aiCreditManager.topupStatus.awaiting_payment", color: "#9e9e9e" },
  pending: { labelKey: "aiCreditManager.topupStatus.pending", color: "#2196f3" },
  credited: { labelKey: "aiCreditManager.topupStatus.credited", color: "#4caf50" },
  failed: { labelKey: "aiCreditManager.topupStatus.failed", color: "#f44336" },
  cancelled: { labelKey: "aiCreditManager.topupStatus.cancelled", color: "#9e9e9e" },
  reversed: { labelKey: "aiCreditManager.topupStatus.reversed", color: "#795548" },
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

const topupStatusLabel = (status) =>
  TOPUP_STATUS[status] ? i18n.t(TOPUP_STATUS[status].labelKey) : status;

// Traduções com <strong>/<code> literais viram elementos (sem dangerouslySetInnerHTML).
const renderRich = (text) =>
  text.split(/(<strong>.*?<\/strong>|<code>.*?<\/code>)/g).map((part, index) => {
    const match = part.match(/^<(strong|code)>(.*)<\/\1>$/);
    if (!match) return part;
    const Tag = match[1];
    return <Tag key={index}>{match[2]}</Tag>;
  });

export default function AiCreditManager() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [reseller, setReseller] = useState(null);
  const [hubError, setHubError] = useState(null);
  const [zeroMarkup, setZeroMarkup] = useState(false);
  // Margem por chamada: mora na revenda do iaHub, não nas settings do CRM.
  const [markupInput, setMarkupInput] = useState("");

  const [settings, setSettings] = useState({
    minCreditValue: "10",
    maxCreditValue: "10000",
    lowBalanceAlert: "10",
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const [linkTarget, setLinkTarget] = useState(null);
  const [hubCompanies, setHubCompanies] = useState([]);
  const [hubCompaniesLoading, setHubCompaniesLoading] = useState(false);
  const [selectedHubCompany, setSelectedHubCompany] = useState("");
  const [hubAgents, setHubAgents] = useState([]);
  const [outOfSync, setOutOfSync] = useState(false);
  const [linking, setLinking] = useState(false);

  const [topups, setTopups] = useState([]);
  const [topupsLoading, setTopupsLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/ai-credits/admin/settings");
      setSettings({
        minCreditValue: String(data.settings?.minCreditValue ?? 10),
        maxCreditValue: String(data.settings?.maxCreditValue ?? 10000),
        lowBalanceAlert: String(data.settings?.lowBalanceAlert ?? 10),
      });
      setConfigured(!!data.configured);
      setReseller(data.reseller || null);
      setMarkupInput(
        data.reseller ? String(Number(data.reseller.markupPercent || 0)) : ""
      );
      setHubError(data.hubError || null);
      setZeroMarkup(!!data.zeroMarkupWarning);
      setOutOfSync(!!data.resellerOutOfSync);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-credits/admin/accounts");
      setCompanies(data.companies || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTopups = useCallback(async () => {
    setTopupsLoading(true);
    try {
      const { data } = await api.get("/ai-credits/admin/topups");
      setTopups(data.topups || []);
    } catch (err) {
      toastError(err);
    } finally {
      setTopupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadAccounts();
    loadTopups();
  }, [loadSettings, loadAccounts, loadTopups]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      // Uma chamada: o backend salva o que é do CRM e sincroniza a revenda no iaHub
      // (margem por chamada, limiar e URL do aviso de saldo).
      const { data } = await api.put("/ai-credits/admin/settings", {
        minCreditValue: Number(settings.minCreditValue),
        maxCreditValue: Number(settings.maxCreditValue),
        lowBalanceAlert: Number(settings.lowBalanceAlert),
        ...(reseller && markupInput !== "" ? { markupPercent: Number(markupInput) } : {}),
      });
      if (data.hubSyncError) {
        toast.warn(
          i18n.t("aiCreditManager.toasts.hubSyncError", { error: data.hubSyncError })
        );
      } else {
        toast.success(i18n.t("aiCreditManager.toasts.settingsSaved"));
      }
      loadSettings();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingSettings(false);
    }
  };

  // Trocar o vínculo com saldo comprado na empresa anterior exige confirmação: o
  // saldo fica lá e as próximas recargas vão para a nova.
  const saveAccount = async (companyId, body) => {
    try {
      return await api.put(`/ai-credits/admin/accounts/${companyId}`, body);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error !== "ERR_AI_CREDIT_LINK_HAS_BALANCE") throw err;

      const confirmed = window.confirm(
        `${data.details || i18n.t("aiCreditManager.confirmLinkChange.defaultDetails")}\n\n` +
          i18n.t("aiCreditManager.confirmLinkChange.message")
      );
      // Cancelou: nada mudou, e não há erro a mostrar.
      if (!confirmed) return null;
      return api.put(`/ai-credits/admin/accounts/${companyId}`, { ...body, force: true });
    }
  };

  const handleToggleEnabled = async (company, enabled) => {
    // Sem vínculo não há para onde creditar: abre o diálogo em vez de gravar
    // um "cobrada" que a recarga vai recusar depois.
    if (enabled && !company.aiHubCompanyId) {
      openLinkDialog(company);
      return;
    }

    try {
      const saved = await saveAccount(company.id, { enabled });
      if (!saved) return;
      toast.success(
        enabled
          ? i18n.t("aiCreditManager.toasts.billingEnabled")
          : i18n.t("aiCreditManager.toasts.billingDisabled")
      );
      loadAccounts();
    } catch (err) {
      toastError(err);
    }
  };

  const openLinkDialog = async (company) => {
    setLinkTarget(company);
    setSelectedHubCompany(company.aiHubCompanyId || "");
    setHubCompanies([]);
    setHubAgents([]);
    setHubCompaniesLoading(true);

    try {
      // O servidor lê na hora todos os prompts da empresa e resolve de quem é cada
      // agente no hub — é dessa empresa que o consumo é descontado.
      const { data } = await api.get("/ai-credits/admin/hub-companies", {
        params: { companyId: company.id },
      });
      setHubCompanies(data.companies || []);
      setHubAgents(data.agents || []);

      // Sugestão só quando todos os agentes são de uma mesma empresa do hub.
      if (!company.aiHubCompanyId && data.suggestedId) {
        setSelectedHubCompany(data.suggestedId);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setHubCompaniesLoading(false);
    }
  };

  const handleSaveLink = async (enable) => {
    if (!linkTarget) return;
    setLinking(true);
    try {
      const saved = await saveAccount(linkTarget.id, {
        aiHubCompanyId: selectedHubCompany || null,
        enabled: enable === undefined ? undefined : enable,
      });
      if (!saved) return;
      toast.success(i18n.t("aiCreditManager.toasts.linkUpdated"));
      setLinkTarget(null);
      loadAccounts();
    } catch (err) {
      toastError(err);
    } finally {
      setLinking(false);
    }
  };

  const handleRetry = async (topup) => {
    setRetryingId(topup.id);
    try {
      const { data } = await api.post(`/ai-credits/admin/topups/${topup.id}/retry`);
      if (data.topup?.hubStatus === "credited") {
        toast.success(i18n.t("aiCreditManager.toasts.topupCredited"));
      } else {
        toast.warn(
          data.topup?.lastError || i18n.t("aiCreditManager.toasts.topupNotAccepted")
        );
      }
      loadTopups();
      loadAccounts();
    } catch (err) {
      toastError(err);
    } finally {
      setRetryingId(null);
    }
  };

  const reversalPending = (topup) =>
    Number(topup.reversedAmount || 0) > Number(topup.hubReversedAmount || 0) &&
    (topup.hubStatus === "credited" || topup.hubStatus === "reversed");
  const pendingTopups = topups.filter(
    (topup) =>
      topup.hubStatus === "failed" ||
      topup.hubStatus === "pending" ||
      reversalPending(topup)
  );

  return (
    <div className={classes.root}>
      {!configured && (
        <Paper className={`${classes.alert} ${classes.danger}`} variant="outlined">
          <Typography variant="body2">
            {renderRich(i18n.t("aiCreditManager.alerts.notConfigured"))}
          </Typography>
        </Paper>
      )}

      {zeroMarkup && (
        <Paper className={`${classes.alert} ${classes.danger}`} variant="outlined">
          <Typography variant="body2">
            {renderRich(
              i18n.t("aiCreditManager.alerts.zeroMarkup", { name: reseller?.name || "" })
            )}
          </Typography>
        </Paper>
      )}

      {outOfSync && !zeroMarkup && (
        <Paper className={`${classes.alert} ${classes.info}`} variant="outlined">
          <Typography variant="body2">
            {renderRich(i18n.t("aiCreditManager.alerts.outOfSync"))}
          </Typography>
        </Paper>
      )}

      {hubError && (
        <Paper className={`${classes.alert} ${classes.info}`} variant="outlined">
          <Typography variant="body2">
            {i18n.t("aiCreditManager.alerts.hubError", { error: hubError })}
          </Typography>
        </Paper>
      )}

      <Paper className={classes.section} variant="outlined">
        <Box className={classes.settingsRow}>
          <Typography variant="subtitle1" style={{ width: "100%" }}>
            {reseller
              ? i18n.t("aiCreditManager.settings.titleWithReseller", { name: reseller.name })
              : i18n.t("aiCreditManager.settings.title")}
          </Typography>

          <TextField
            label={i18n.t("aiCreditManager.settings.markup")}
            type="number"
            size="small"
            variant="outlined"
            value={markupInput}
            onChange={(e) => setMarkupInput(e.target.value)}
            disabled={!reseller}
            inputProps={{ min: 0, max: 500, step: "0.01" }}
            style={{ width: 190 }}
          />
          <TextField
            label={i18n.t("aiCreditManager.settings.minCredit")}
            type="number"
            size="small"
            variant="outlined"
            value={settings.minCreditValue}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, minCreditValue: e.target.value }))
            }
            inputProps={{ min: 1, step: "0.01" }}
            style={{ width: 150 }}
          />
          <TextField
            label={i18n.t("aiCreditManager.settings.maxCredit")}
            type="number"
            size="small"
            variant="outlined"
            value={settings.maxCreditValue}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, maxCreditValue: e.target.value }))
            }
            inputProps={{ min: 1, step: "0.01" }}
            style={{ width: 150 }}
          />
          <TextField
            label={i18n.t("aiCreditManager.settings.lowBalanceAlert")}
            type="number"
            size="small"
            variant="outlined"
            value={settings.lowBalanceAlert}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, lowBalanceAlert: e.target.value }))
            }
            inputProps={{ min: 0, step: "0.01" }}
            style={{ width: 180 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSettings}
            disabled={savingSettings}
          >
            {savingSettings ? i18n.t("common.saving") : i18n.t("common.save")}
          </Button>

          <Typography variant="caption" color="textSecondary" style={{ width: "100%" }}>
            {reseller
              ? i18n.t("aiCreditManager.settings.hintWithReseller", {
                  amount: formatCurrency(100),
                  name: reseller.name,
                })
              : i18n.t("aiCreditManager.settings.hint", { amount: formatCurrency(100) })}
            {!reseller && ` ${i18n.t("aiCreditManager.settings.noReseller")}`}
          </Typography>
        </Box>
      </Paper>

      {pendingTopups.length > 0 && (
        <Paper className={classes.section} variant="outlined">
          <Box p={2}>
            <Typography variant="subtitle1" gutterBottom>
              {i18n.t("aiCreditManager.pending.title")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {i18n.t("aiCreditManager.pending.hint")}
            </Typography>
            <div className={classes.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("common.date")}</TableCell>
                    <TableCell>{i18n.t("common.company")}</TableCell>
                    <TableCell>{i18n.t("aiCreditManager.pending.status")}</TableCell>
                    <TableCell align="right">{i18n.t("aiCreditManager.pending.credit")}</TableCell>
                    <TableCell align="right">{i18n.t("aiCreditManager.pending.attempts")}</TableCell>
                    <TableCell>{i18n.t("common.error")}</TableCell>
                    <TableCell align="right">{i18n.t("aiCreditManager.pending.action")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingTopups.map((topup) => (
                    <TableRow key={topup.id}>
                      <TableCell>
                        {moment(topup.createdAt).format("DD/MM/YY HH:mm")}
                      </TableCell>
                      <TableCell>
                        {companies.find((c) => c.id === topup.companyId)?.name ||
                          topup.companyId}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            reversalPending(topup)
                              ? i18n.t("aiCreditManager.topupStatus.reversalPending")
                              : `${topupStatusLabel(topup.hubStatus)}${
                                  topup.hubStatus === "credited" &&
                                  Number(topup.hubReversedAmount) > 0
                                    ? ` (${i18n.t("aiCreditManager.topupStatus.reversedAmount", {
                                        amount: formatCurrency(topup.hubReversedAmount),
                                      })})`
                                    : ""
                                }`
                          }
                          style={{
                            backgroundColor:
                              (TOPUP_STATUS[topup.hubStatus] || {}).color ||
                              "#9e9e9e",
                            color: "#fff",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(topup.creditAmount)}
                      </TableCell>
                      <TableCell align="right">{topup.attempts}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{topup.lastError}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={retryingId === topup.id}
                          onClick={() => handleRetry(topup)}
                        >
                          {retryingId === topup.id
                            ? i18n.t("aiCreditManager.pending.resending")
                            : i18n.t("aiCreditManager.pending.resend")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Box>
        </Paper>
      )}

      <Paper className={classes.section} variant="outlined">
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            {i18n.t("aiCreditManager.companies.title")}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {i18n.t("aiCreditManager.companies.hint")}
          </Typography>
        </Box>

        {loading || topupsLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <div className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("common.company")}</TableCell>
                  <TableCell>{i18n.t("aiCreditManager.companies.billing")}</TableCell>
                  <TableCell>{i18n.t("aiCreditManager.hubCompany")}</TableCell>
                  <TableCell align="right">{i18n.t("aiCreditManager.companies.balance")}</TableCell>
                  <TableCell>{i18n.t("aiCreditManager.companies.updatedAt")}</TableCell>
                  <TableCell align="right">{i18n.t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>{company.name}</TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        color="primary"
                        checked={company.enabled}
                        onChange={(e) =>
                          handleToggleEnabled(company, e.target.checked)
                        }
                      />
                      <Chip
                        size="small"
                        label={
                          company.enabled
                            ? i18n.t("aiCreditManager.companies.prepaid")
                            : i18n.t("aiCreditManager.companies.courtesy")
                        }
                        style={{
                          backgroundColor: company.enabled ? "#4caf50" : "#9e9e9e",
                          color: "#fff",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {company.aiHubCompanyId ? (
                        <>
                          <Typography variant="caption">
                            {company.aiHubCompanyId}
                          </Typography>
                          {(company.agentWarnings || []).map((warning) => (
                            <Typography
                              key={warning.agentId}
                              variant="caption"
                              display="block"
                              style={{ color: "#f44336" }}
                            >
                              {warning.hubCompanyName
                                ? i18n.t("aiCreditManager.companies.agentOtherCompany", {
                                    prompts: warning.promptNames.join(", "),
                                    company: warning.hubCompanyName,
                                  })
                                : i18n.t("aiCreditManager.companies.agentMissing", {
                                    prompts: warning.promptNames.join(", "),
                                  })}
                            </Typography>
                          ))}
                        </>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {i18n.t("aiCreditManager.companies.notLinked")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {company.lastKnownBalance === null
                        ? "-"
                        : formatCurrency(company.lastKnownBalance)}
                      {company.lastKnownBlocked && (
                        <Chip
                          size="small"
                          label={i18n.t("aiCreditManager.companies.blocked")}
                          style={{
                            backgroundColor: "#f44336",
                            color: "#fff",
                            marginLeft: 4,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {company.balanceSyncedAt
                        ? moment(company.balanceSyncedAt).format("DD/MM/YY HH:mm")
                        : "-"}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openLinkDialog(company)}>
                        {i18n.t("aiCreditManager.companies.link")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {companies.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("aiCreditManager.companies.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Paper>

      <Dialog
        open={!!linkTarget}
        onClose={() => setLinkTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("aiCreditManager.linkDialog.title", { name: linkTarget?.name || "" })}
        </DialogTitle>
        <DialogContent>
          {hubCompaniesLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                label={i18n.t("aiCreditManager.hubCompany")}
                value={selectedHubCompany}
                onChange={(e) => setSelectedHubCompany(e.target.value)}
                margin="dense"
              >
                <MenuItem value="">
                  <em>{i18n.t("aiCreditManager.linkDialog.noLink")}</em>
                </MenuItem>
                {hubCompanies.map((hubCompany) => (
                  <MenuItem key={hubCompany.id} value={hubCompany.id}>
                    {hubCompany.name} — {formatCurrency(hubCompany.balanceBRL)}
                    {hubCompany.confirmed
                      ? ""
                      : ` ${i18n.t("aiCreditManager.linkDialog.notConfirmed")}`}
                    {hubCompany.agentOwner
                      ? ` · ${i18n.t("aiCreditManager.linkDialog.agentOwner")}`
                      : ""}
                  </MenuItem>
                ))}
              </TextField>

              <Typography variant="caption" color="textSecondary" display="block">
                {i18n.t("aiCreditManager.linkDialog.hint")}
              </Typography>

              {hubAgents.length === 0 ? (
                <Typography variant="caption" color="textSecondary" display="block">
                  {i18n.t("aiCreditManager.linkDialog.noAgents")}
                </Typography>
              ) : (
                hubAgents.map((agent) => (
                  <Typography
                    key={agent.agentId}
                    variant="caption"
                    display="block"
                    style={{
                      color:
                        selectedHubCompany && agent.hubCompanyId !== selectedHubCompany
                          ? "#f44336"
                          : undefined,
                    }}
                  >
                    {agent.hubCompanyName
                      ? i18n.t("aiCreditManager.linkDialog.agentOf", {
                          prompts: agent.promptNames.join(", "),
                          company: agent.hubCompanyName,
                        })
                      : i18n.t("aiCreditManager.linkDialog.agentOfUnknown", {
                          prompts: agent.promptNames.join(", "),
                        })}
                  </Typography>
                ))
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkTarget(null)} disabled={linking}>
            {i18n.t("common.cancel")}
          </Button>
          <Button
            onClick={() => handleSaveLink(undefined)}
            disabled={linking}
            color="primary"
          >
            {i18n.t("aiCreditManager.linkDialog.saveLink")}
          </Button>
          <Button
            onClick={() => handleSaveLink(true)}
            disabled={linking || !selectedHubCompany}
            color="primary"
            variant="contained"
          >
            {i18n.t("aiCreditManager.linkDialog.saveAndCharge")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
